import { useCallback } from 'react';

import { AxiosResponse } from 'axios';
import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import { PaginatedWorkoutHistoryEntryList, UrlParams } from 'types';
import { client } from 'utils/api-client';
import { encodeUrlParams, urlParamsFromUrl } from 'utils/functions';

import { MovementHistoryKey } from './movementHistoryKey';

interface QueryKeyType {
  pageParam?: UrlParams;
}

/** One timeline's entries, newest first, a page at a time. */
function useMovementHistoryEntries(
  { athleteId, movementId, scoreType }: MovementHistoryKey,
  { limit = '20', enabled = true }: { limit?: string; enabled?: boolean } = {},
): UseInfiniteQueryResult<AxiosResponse<PaginatedWorkoutHistoryEntryList>> {
  const fetchEntries = useCallback(
    ({
      pageParam = { limit, offset: '0' },
    }: QueryKeyType): Promise<AxiosResponse<PaginatedWorkoutHistoryEntryList>> => {
      const params: UrlParams = {
        ...pageParam,
        athlete: athleteId,
        movement: movementId as string,
        score_type: scoreType as string,
      };
      return client.get(`workout-history/movement/entries/?${encodeUrlParams(params)}`);
    },
    [athleteId, limit, movementId, scoreType],
  );

  return useInfiniteQuery(
    ['workout-history', 'movement', 'entries', athleteId, movementId, scoreType, limit],
    fetchEntries,
    {
      enabled: enabled && !!athleteId && !!movementId && !!scoreType,
      getNextPageParam: page => (page.data.next ? urlParamsFromUrl(page.data.next) : undefined),
    },
  );
}

export default useMovementHistoryEntries;
